import { useSelector } from "react-redux";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, Link } from 'react-router-dom'
import { useTenantNavigation, useTenantUrl } from '../../hooks/useTenantNavigation.js'
import { Row, Col, Button, Tag, Tooltip, Input, Modal, Popover, message, Segmented, Dropdown } from "antd";
import TableComponent from "./components/table/Index";
import KanbanBoard from "./components/KanbanBoard";
import {
    AppstoreOutlined,
    BarChartOutlined,
    BranchesOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    DeleteOutlined,
    DownloadOutlined,
    DownOutlined,
    EditOutlined,
    FileTextOutlined,
    FilterOutlined,
    HomeOutlined,
    InboxOutlined,
    PlusOutlined,
    RightOutlined,
    SaveOutlined,
    SettingOutlined,
    TableOutlined, UnorderedListOutlined, UserOutlined,
    StarOutlined, StarFilled, MoreOutlined
} from '@ant-design/icons'
import { SearchOutlined } from '@ant-design/icons'
import "../../style/documents.css"
import "../../style/kanban.css"
import axios from '../../axios-interceptor.js'
import BreadCrumb from "../BreadCrumb";
import ColumnsSelector from "./components/ColumnsSelector";
import SearchContainer from "./components/SearchContainer";
import ViewSelector from "./components/ViewSelector";
import { getCurrentSpaceData, getUserSpaceEntitlements, getUserSelectedView } from "../../utils/currentUserDetail";
import moment from "moment";
import { safeMoment } from "../../utils/safeDate.js";
import exportExcel from "../../utils/exportExcel";
import { useTranslation } from "react-i18next";
import { showLoader } from "../../utils/showloader";
import { getSpaceWorkflow, getAllFormConditionForWorkflow } from "../../services/WorkflowServices";
import { deleteSpaceView, getRequests, getSpaceAllFormsByWorkflowId, getSpaceViews, getSingleView, getRequestsForGraph, getAdvanceSearchDocuments, getSingleRequest, massUpdateRequests, updateSelectedView } from "../../services/requestServices";
import { getAllWidgets } from "../../utils/formsHelper";
import { formatViewSelectedColumns, getFormattedRequestValues, getDifferenceValues, getVisibleWidgets } from "../../utils/requestHelper";
import { tagColorMapping, sortNodesByEdges, getWorkflowStatusOptions } from "../../utils/workflowHelper";
import { resolveNodeTranslation } from "../../utils/workflowTranslations";
import { resolveFieldTranslation } from "../../utils/formTranslations";
import { fetchSpaceSpecificGroups, fetchSpaceSpecificUserPickers, fetchSpaceSpecificUsers } from "../../redux/actions/userRoleGroupAction";
import { useDispatch } from "react-redux";
import { statusColorMapping } from "../../utils/colorBgColorMap";

const BACKEND_API = process.env.REACT_APP_BACKEND_API || "";
let tenantId = localStorage.getItem("tenantId")

// Resolve the widget list to use when formatting a single request row.
// Each request carries its own `workflowForms` snapshot (taken at submission time),
// whose option ids match the values stored on that row. Prefer those per-row widgets
// so dropdown/radio/multi-select values resolve to their labels instead of showing the
// raw option ids — the current form's `definedWidgets` may have been edited since and no
// longer match older rows. Falls back to `definedWidgets` when the row has no snapshot.
const getRowFormWidgets = (request, fallbackWidgets = []) => {
    const forms = Array.isArray(request?.workflowForms) ? request.workflowForms : []
    let items = []
    forms.forEach(form => {
        if (Array.isArray(form?.items)) items = items.concat(form.items)
    })
    const rowWidgets = items.length ? getAllWidgets(items) : []
    if (!rowWidgets.length) return fallbackWidgets

    const byId = new Map()
    rowWidgets.forEach(widget => { if (widget?.id) byId.set(widget.id, widget) })
        ; (fallbackWidgets || []).forEach(widget => {
            if (widget?.id && !byId.has(widget.id)) byId.set(widget.id, widget)
        })
    return Array.from(byId.values())
}

// A request that was turned down is stored as "rejected" on some workflows and
// "refused" on others (step statuses use "refused"), so both spellings mean the
// same thing everywhere the rejection details are read.
const REJECTED_STATUSES = ["rejected", "refused"]
const isRejectedStatus = (value) => REJECTED_STATUSES.includes(String(value || "").toLowerCase())

// `updatedOn` arrives in two shapes depending on when the action was recorded:
// ISO ("2026-05-29T00:15:13.520") and JS date strings ("Fri May 29 2026 08:43:40
// GMT+0000"). Try ISO strictly first so moment never falls back to its deprecated
// loose parser (which logs a warning for every row).
const parseActionDate = (value) => {
    if (!value) return null
    const iso = moment(value, moment.ISO_8601, true)
    if (iso.isValid()) return iso
    const asDate = new Date(value)
    return isNaN(asDate.getTime()) ? null : moment(asDate)
}

const formatActionDate = (value) => {
    const parsed = parseActionDate(value)
    return parsed ? parsed.format("MMM DD, YYYY hh:mm A") : (value ? String(value) : "")
}

// Who refused a request, at which step, when and why.
// Only the dashboard API ships a ready-made `refusedBy`; the space requests API
// returns the full `workflow` snapshot instead, where the refusal lives on the
// step's `statuses` entry. Walk that first and keep the most recent refusal — a
// request can be sent back more than once across cycles.
const getRejectionDetails = (record) => {
    if (!record) return null

    const steps = Array.isArray(record.workflow) ? record.workflow : []
    let latest = null
    steps.forEach(step => {
        const statuses = Array.isArray(step?.statuses) ? step.statuses : []
        statuses.forEach(entry => {
            if (!isRejectedStatus(entry?.approvalStatus)) return
            const actionedAt = parseActionDate(entry?.updatedOn)
            const time = actionedAt ? actionedAt.valueOf() : 0
            if (latest && time < latest.time) return
            latest = {
                time,
                stepName: step?.name || "",
                actionBy: entry?.actionBy || entry?.approverValue || "",
                updatedOn: entry?.updatedOn || "",
                comment: entry?.comment || ""
            }
        })
    })
    if (latest) return latest

    if (record.refusedBy?.actionBy || record.refusedBy?.updatedOn) {
        return {
            stepName: record.refusedBy.stepName || "",
            actionBy: record.refusedBy.actionBy || "",
            updatedOn: record.refusedBy.updatedOn || "",
            comment: record.refusedBy.comment || ""
        }
    }

    // Older rows only flag the refusal on the step itself, with no status entry —
    // the step name is still worth showing.
    const refusedStep = steps.find(step => isRejectedStatus(step?.approvalStatus))
    if (refusedStep) {
        return { stepName: refusedStep.name || "", actionBy: "", updatedOn: "", comment: "" }
    }
    return null
}

// Rejected status tag with a popover detailing the refusal. Rows coming from a
// view-scoped list response can omit the `workflow` snapshot, so when nothing can
// be derived locally the full request is fetched once, the first time the popover
// is opened.
const RejectedStatusTag = ({ record, label, language }) => {
    const derivedDetails = useMemo(() => getRejectionDetails(record), [record])
    const [fetchedDetails, setFetchedDetails] = useState(null)
    const [isLoadingDetails, setIsLoadingDetails] = useState(false)
    const hasFetchedRef = useRef(false)
    const details = derivedDetails || fetchedDetails

    const stepLabel = useMemo(() => {
        if (!details?.stepName) return ""
        const node = (record?.originalWorkflow?.nodes || []).find(item => item?.data?.name === details.stepName)
        return node ? (resolveNodeTranslation(node.data, language)?.name || details.stepName) : details.stepName
    }, [details, record, language])

    const handleOpenChange = async (open) => {
        if (!open || details || hasFetchedRef.current || !record?.requestId) return
        hasFetchedRef.current = true
        setIsLoadingDetails(true)
        const response = await getSingleRequest(record.requestId)
        setIsLoadingDetails(false)
        if (response?.status === 200 && response?.data) {
            setFetchedDetails(getRejectionDetails(response.data))
        }
    }

    const content = (
        <div style={{ fontSize: 13, padding: "10px 10px", maxWidth: 300, wordBreak: "break-word" }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {stepLabel ? `Refused at ${stepLabel}` : "Refused"}
            </div>
            {details?.actionBy && (
                <div><UserOutlined style={{ color: "rgba(22, 119, 255, 1)" }} /> {details.actionBy}</div>
            )}
            {details?.updatedOn && (
                <div><CalendarOutlined style={{ color: "rgba(22, 119, 255, 1)" }} /> {formatActionDate(details.updatedOn)}</div>
            )}
            {details?.comment && (
                <div style={{ color: "grey", fontStyle: "italic", marginTop: 4, whiteSpace: "pre-wrap" }}>{details.comment}</div>
            )}
            {!details && (
                <div style={{ color: "grey" }}>{isLoadingDetails ? "Loading details..." : "Rejection details are not available."}</div>
            )}
        </div>
    )

    return (
        <Popover content={content} placement="top" onOpenChange={handleOpenChange}>
            <Tag color="red" className="request-status-tag" style={{ textTransform: "capitalize" }} bordered={false}>
                {label}
            </Tag>
        </Popover>
    )
}

const WrapperStyle = {
    margin: "32px 40px 25px",
};

const defaultView = {
    label: "Default",
    value: "default",
    selectedColumns: [
        { label: "Request ID", id: "caseId" },
        { label: "Status", id: "status" },
        { label: "Creation Date", id: "createdOn" },
        { label: "Requester", id: "createdBy" },
        { label: "Current Cycle", id: "currentCycle" }
    ],
    appliedFilters: []
}

const docTableConfig = {
    type: "table",
    id: "documents_table",
    selectable: false,
    columns: []
}

const columnsNotToIncludeInView = ["addressField", "attachment", "imageForm", "infoForm", "tableForm", "divider", "section"]

const TRUNCATE_LENGTH = 30

const TruncatedCell = ({ text, label = "Details" }) => {
    const { t } = useTranslation();
    const displayText = text ? String(text) : "";
    const isLong = displayText.length > TRUNCATE_LENGTH;
    const truncated = isLong ? displayText.slice(0, TRUNCATE_LENGTH) + '...' : displayText;

    const popoverContent = (
        <div style={{ width: 360, maxHeight: '60vh', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', padding: "12px" }}>
            <div style={{ fontWeight: "600", fontSize: "16px" }}>{label}</div>
            {displayText}
        </div>
    );

    return (
        <span>
            <Tooltip title={displayText || "-"}>
                <span>{truncated || "-"}</span>
            </Tooltip>
            {isLong && (
                <Popover
                    content={popoverContent}
                    trigger="click"
                    placement="topLeft"
                    styles={{ body: { padding: 16 } }}
                >
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginLeft: 6, color: '#1677ff', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                        {t('View More')}
                    </span>
                </Popover>
            )}
        </span>
    );
};

// Column config for any dynamic (widget) column — text, textarea, etc. Keeps the
// truncate + "View More" behaviour identical no matter how the column got added
// (saved view or the columns selector).
const buildTruncatedColumn = (dataIndex, label) => ({
    title: label,
    dataIndex,
    ellipsis: { showTitle: false },
    render: (text) => <TruncatedCell text={text} label={label} />,
});

// Column title in the user's default language: form `translations` first, then
// the i18n catalogue (so shared names like "Supplier Name" still localise).
const getWidgetDisplayLabel = (widget, language, t) => {
    if (!widget) return ""

    const localizeText = (text) => {
        if (typeof text !== "string" || !text.trim()) return text || ""
        if (typeof t !== "function") return text
        const localized = t(text, { lng: language })
        return localized && localized !== text ? localized : text
    }

    if (widget.parentTableId && widget.parentTable) {
        const localizedTable = resolveFieldTranslation(widget.parentTable, language)
        const columnId = String(widget.id).includes("::")
            ? String(widget.id).split("::").pop()
            : widget.id
        const localizedColumn = (localizedTable?.columns || []).find(
            (col) => String(col?.id ?? col?._id) === String(columnId)
        ) || (localizedTable?.columns || []).find(
            (col) => widget.actualLabel && String(col?.label) === String(widget.actualLabel)
        )
        const canonicalColumn = widget.actualLabel || widget.label
        const formColumnLabel = localizedColumn?.label
        const columnLabel = (formColumnLabel && formColumnLabel !== canonicalColumn)
            ? formColumnLabel
            : localizeText(canonicalColumn)
        const canonicalTable = widget.parentTable.label
        const formTableLabel = localizedTable?.label
        const tableLabel = (formTableLabel && formTableLabel !== canonicalTable)
            ? formTableLabel
            : localizeText(canonicalTable)
        return tableLabel ? `${columnLabel} (${tableLabel})` : columnLabel
    }

    const canonical = widget.label || ""
    const formLabel = resolveFieldTranslation(widget, language)?.label
    if (formLabel && formLabel !== canonical) return formLabel
    return localizeText(canonical)
}

const translateSectionLabel = (section, language, t) => {
    const canonical = typeof section === "string" ? section : (section?.label || "")
    if (!canonical) return ""
    if (section && typeof section === "object") {
        const formLabel = resolveFieldTranslation(section, language)?.label
        if (formLabel && formLabel !== canonical) return formLabel
    }
    if (typeof t !== "function") return canonical
    const localized = t(canonical, { lng: language })
    return localized && localized !== canonical ? localized : canonical
}

// Top parent + immediate subsection: "Parent Section - Subsection".
const getWidgetParentSectionLabel = (widget, language, t) => {
    const sections = Array.isArray(widget?.parentSections) ? widget.parentSections : []
    if (sections.length) {
        const labels = sections.map(section => translateSectionLabel(section, language, t)).filter(Boolean)
        if (!labels.length) return ""
        if (labels.length === 1) return labels[0]
        return `${labels[0]} - ${labels[labels.length - 1]}`
    }
    const section = widget?.parentSection
    const canonical = section?.label
        || (widget?.parentTableId ? widget?.parentTable?.sectionLabel : widget?.sectionLabel)
        || ""
    return translateSectionLabel(section || canonical, language, t)
}

// ---- Per-user, per-space default view mode (List / Kanban / Graph) ----
// Persisted server-side via PUT /selectedView; localStorage is kept as an
// optimistic cache so the saved default applies instantly on reload.
const VIEW_MODE_PREF_VERSION = 'ff_default_viewmode_v1';
const VALID_DEFAULT_MODES = ['table', 'kanban', 'graph'];
// Frontend view-mode <-> backend selectedView label mapping.
const VIEW_MODE_TO_API = { table: 'List', kanban: 'Kanban', graph: 'Graph' };
const API_TO_VIEW_MODE = { List: 'table', Kanban: 'kanban', Graph: 'graph' };
const currentUserId = () => localStorage.getItem('userId') || localStorage.getItem('id') || 'x';
const viewModePrefKey = (userId, spaceId) => `${VIEW_MODE_PREF_VERSION}::${userId || 'x'}::${spaceId || 'x'}`;
const readDefaultViewMode = (userId, spaceId) => {
    try {
        const v = localStorage.getItem(viewModePrefKey(userId, spaceId));
        return VALID_DEFAULT_MODES.includes(v) ? v : null;
    } catch { return null; }
};
const writeDefaultViewMode = (userId, spaceId, mode) => {
    try { localStorage.setItem(viewModePrefKey(userId, spaceId), mode); } catch { /* storage unavailable */ }
};
const clearDefaultViewMode = (userId, spaceId) => {
    try { localStorage.removeItem(viewModePrefKey(userId, spaceId)); } catch { /* storage unavailable */ }
};

const DocumentsList = () => {
    const { spaceId } = useParams();
    const { t, i18n } = useTranslation();
    // const spaces = useContext(SpaceContext);
    const navigate = useTenantNavigation();
    const buildUrl = useTenantUrl();
    // removed unused searchWrapperRef
    const dispatch = useDispatch();

    // removed unused spaces state (was never read)
    const [breadcrumbs, setBreadCrumbs] = useState([])
    const selectedSpace = spaceId
    const [documents, setDocuments] = useState([])
    const [viewModal, setViewModal] = useState(false)
    // Initialize the view mode from the user's saved per-space default (falls back to List)
    const [currentViewMode, setCurrentViewMode] = useState(() => readDefaultViewMode(currentUserId(), spaceId) || 'table')
    const [defaultViewMode, setDefaultViewMode] = useState(() => readDefaultViewMode(currentUserId(), spaceId))
    const [workflow, setWorkflow] = useState({})
    const [showSearch, setShowSearch] = useState(false)
    const [searchKey, setSearchKey] = useState('')
    const [isSearchLoading, setIsSearchLoading] = useState(false)
    const [selectedView, setSelectedView] = useState({})
    const [spaceViews, setSpaceViews] = useState([]) // Currently selected view only
    const [availableViews, setAvailableViews] = useState([]) // All available views for dropdown
    const [isTableLoading, setIsTableLoading] = useState(false)

    const [tableConfig, setTableConfig] = useState(docTableConfig)
    const [selectedFilters, setSelectedFilters] = useState({})
    const [selectedColumns, setSelectedColumns] = useState([])
    const [filtersChanged, setFiltersChanged] = useState(false)
    const [isManualViewSelection, setIsManualViewSelection] = useState(false)
    const [isViewColumnsLoading, setIsViewColumnsLoading] = useState(false)
    const [initialDefaultView, setInitialDefaultView] = useState({})
    const [currentSpaceObj, setCurrentSpaceObj] = useState({})
    const [spaceEntitlements, setSpaceEntitlements] = useState([])
    const [definedWidgets, setDefinedWidgets] = useState([])
    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1)
    const [totalRecords, setTotalRecords] = useState(0)
    const [statusCounts, setStatusCounts] = useState([])
    const [showStats, setShowStats] = useState(false)
    const [workflowIsCompleted, setWorkflowIsCompleted] = useState(false)
    const [graphData, setGraphData] = useState(null)
    const [openAdvanceSearchModal, setOpenAdvanceSearchModal] = useState(false)
    const [selectedRowKeys, setSelectedRowKeys] = useState([])
    const [selectedRows, setSelectedRows] = useState([])
    const [editedRows, setEditedRows] = useState({})
    const [selectionLoadingKeys, setSelectionLoadingKeys] = useState([])
    const [isBulkUpdating, setIsBulkUpdating] = useState(false)
    const [formConditions, setFormConditions] = useState([])
    const [searchParams, setSearchParams] = useState({})
    const { userInfo, userGroupSpaces } = useSelector(state => state.user)
    const { spaceSpecificUserPickers = [], spaceSpecificUsers = [], spaceSpecificGroups = [] } = useSelector((state) => state.userRoleGroupState)
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isSearchDone, setIsSearchDone] = useState(false);
    const fetchedWorkflowRef = useRef(new Set());
    // Tracks spaces whose server-side default view has already been applied,
    // so it doesn't override a manual view switch on re-render.
    const appliedSpaceDefaultRef = useRef(new Set());

    // The Status filter lists every status a request can be in — static ones plus
    // each workflow step — instead of only the values present on the loaded page.
    // Same set the advance-search status field offers.
    const statusFilters = useMemo(() => (
        getWorkflowStatusOptions(workflow, { language: i18n.language, t })
            .map(option => ({ text: option.label, value: option.value }))
    ), [workflow, i18n.language, t])

    const allColumns = useMemo(() => ([
        {
            title: t('Request ID'),
            dataIndex: 'caseId',
            key: 'caseId',
            no_filter: true,
            fixed: 'left',
            render: (text, rec) => {
                let redirectPage = rec.status === "draft" ? "submission" : "approve"
                return <Link to={buildUrl(`/app/space/${selectedSpace}/${redirectPage}/${rec.workflowId}/${rec.requestId}`)} style={{ textDecorationLine: "underline", color: "rgba(0, 0, 0, 0.65)", }}>{text}</Link>
            }
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            filters: statusFilters,
            render: (text, rec) => {
                let recNodes = rec?.originalWorkflow?.nodes || []
                let curStep = recNodes.find(step => step.data.name === text);
                let curStepColor = curStep?.data?.stepColor || "#0A99FE"
                curStepColor = statusColorMapping[text] || "#0A99FE"
                let isEndStep = recNodes?.find(node => node.data?.type === "end" && node.data?.name === text) || false
                curStepColor = isEndStep ? statusColorMapping["approved"] : curStepColor

                // Show the step label in the viewer's language; canonical `text`
                // still drives color/matching so status logic is unaffected.
                // Statuses that aren't workflow steps (draft, archived, rejected, …)
                // use i18n — locale keys are capitalized ("Draft"), stored values are not.
                let displayStatus = curStep
                    ? (resolveNodeTranslation(curStep.data, i18n.language)?.name || text)
                    : (text ? t(`${String(text).charAt(0).toUpperCase()}${String(text).slice(1)}`) : text)

                if (isRejectedStatus(text)) {
                    return <RejectedStatusTag record={rec} label={displayStatus} language={i18n.language} />
                }
                return (
                    (
                        <Tooltip title={displayStatus}>
                            <Tag
                                color={tagColorMapping[curStepColor] || curStepColor || "#0A99FE"}
                                className="request-status-tag"
                                style={{ textTransform: "capitalize" }}
                                bordered={false}
                            >
                                <span className="request-status-text">{displayStatus}</span>
                            </Tag>
                        </Tooltip>
                    )
                )
            },
        },
        {
            title: t('Current Cycle'),
            dataIndex: 'currentCycle',
            key: 'currentCycle',
            align: 'center',
        },
        {
            title: t('Requester'),
            dataIndex: 'createdBy',
            key: 'createdBy',
            custom_search: true,
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={record.createdByEmail || text}>
                    <TruncatedCell text={text || record.createdBy?.name} label={t('Requester')} />
                </Tooltip>
            )
        },
        {
            title: t('Creation Date'),
            dataIndex: 'createdOn',
            key: 'createdOn',
            isDate: true,
            sorter: (a, b) => { return a.createdOn.localeCompare(b.createdOn) },
            date_filter: true
        }
    ]), [t, workflow, selectedSpace, i18n.language, statusFilters])

    const viewerLanguage = userInfo?.defaultLanguage || i18n.language

    // Catalogue for Customize Columns: system fields via i18n, form fields via
    // the form's translations for the user's default language.
    const defaultViewColumns = useMemo(() => {
        const reqCols = []
        defaultView.selectedColumns.forEach((col) => {
            const colObj = {
                label: t(col.label, { lng: viewerLanguage }),
                value: col.id,
                isDraggable: col.id !== "caseId",
                isDisabled: col.id === "caseId",
                id: col.id,
                formType: col.formType
            }
            if (col.id === "caseId") {
                reqCols.unshift(colObj)
            } else {
                reqCols.push(colObj)
            }
        })
        definedWidgets.forEach((col) => {
            reqCols.push({
                label: getWidgetDisplayLabel(col, viewerLanguage, t) || col.label,
                sectionLabel: getWidgetParentSectionLabel(col, viewerLanguage, t),
                value: col.id,
                isDraggable: true,
                isDisabled: false,
                id: col.id,
                formType: col.formType
            })
        })
        return reqCols
    }, [definedWidgets, t, viewerLanguage])

    const kanbanStatuses = useMemo(() => {
        const rawNodes = Array.isArray(workflow?.node) ? workflow.node : []
        const edges = Array.isArray(workflow?.edge) ? workflow.edge : []

        // Sort workflow nodes topologically so kanban columns follow the actual flow order
        const sortedNodes = edges.length > 0 ? sortNodesByEdges(rawNodes, edges) : rawNodes

        const workflowStatuses = sortedNodes
            .filter((node) => node?.type === 'default' || node?.type === 'textInput' || node?.data?.type === 'end')
            .map((node) => {
                const status = node?.data?.name
                const isEndStep = node?.data?.type === 'end'
                return {
                    status,
                    label: status,
                    color: isEndStep ? (statusColorMapping.approved || '#50A248') : (node?.data?.stepColor || '#eb2f96'),
                }
            })
            .filter((item) => item.status)

        const countMap = (statusCounts || []).reduce((acc, item) => {
            if (item?.status) {
                acc[item.status] = item.count || 0
            }
            return acc
        }, {})

        const dynamicStatuses = [
            ...(statusCounts || []).map((item) => item?.status).filter(Boolean),
            ...(documents || []).map((doc) => doc?.status).filter(Boolean),
        ]

        // Start with workflow-ordered statuses, then append any extra statuses not in the workflow
        const merged = [...workflowStatuses]
        dynamicStatuses.forEach((status) => {
            if (!merged.some((entry) => entry.status === status)) {
                merged.push({
                    status,
                    label: status,
                    color: statusColorMapping[status?.toLowerCase()] || '#eb2f96',
                })
            }
        })

        const withCounts = merged.map((item) => ({
            ...item,
            count: countMap[item.status] || 0,
        }))

        // Draft is not a workflow node — always pin it first
        const draftIndex = withCounts.findIndex((item) => item.status?.toLowerCase() === 'draft')
        if (draftIndex > 0) {
            const [draftItem] = withCounts.splice(draftIndex, 1)
            withCounts.unshift(draftItem)
        }

        return withCounts
    }, [workflow, statusCounts, documents])

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        if (userInfo && userInfo.userId) {
            let spaceObj = getCurrentSpaceData(spaceId, userInfo.spaceInfo, userGroupSpaces)
            if (spaceObj) {
                setCurrentSpaceObj(spaceObj)
                // Apply the server-persisted default view (once per space) so the
                // saved List/Kanban/Graph preference is honored on load. The value
                // lives on the space's userAccess entry (selectedView).
                const serverMode = API_TO_VIEW_MODE[getUserSelectedView(spaceId, userInfo.userAccess)]
                if (serverMode && !appliedSpaceDefaultRef.current.has(spaceId)) {
                    appliedSpaceDefaultRef.current.add(spaceId)
                    const uid = userInfo?.userId || currentUserId()
                    writeDefaultViewMode(uid, spaceId, serverMode)
                    setDefaultViewMode(serverMode)
                    setCurrentViewMode(serverMode)
                }
            }
            let entitlements = getUserSpaceEntitlements(selectedSpace, userInfo.userAccess, userGroupSpaces)

            setSpaceEntitlements(entitlements)
        }
    }, [userInfo, selectedSpace, userGroupSpaces, spaceId])

    useEffect(() => {
        if (userInfo.userId) {
            dispatch(fetchSpaceSpecificGroups({ spaceId, userInfo }, false))
            dispatch(fetchSpaceSpecificUserPickers({ spaceId, userInfo }, true))
            setTimeout(() => {
                dispatch(fetchSpaceSpecificUsers({ spaceId, userInfo }, false))
            }, [500])
        }
    }, [userInfo, dispatch, spaceId])

    useEffect(() => {
        // Keep the selected view as-is when the user modifies columns/filters —
        // we intentionally do NOT auto-switch to "Custom View" anymore.
        if (isManualViewSelection) {
            const timeout = setTimeout(() => setIsManualViewSelection(false), 120);
            return () => clearTimeout(timeout);
        }
    }, [filtersChanged, selectedView?.value, isManualViewSelection, isViewColumnsLoading])

    const getAllDefinedWidgets = useCallback(async () => {
        let formData = await getSpaceAllFormsByWorkflowId(workflow.workflowId, userInfo);
        let formElements = []
        if (formData?.data?.length > 0) {
            let items = []
            formData.data.forEach(formItem => {
                if (formItem.items) {
                    items = items.concat([...formItem.items])
                }
            })
            const allWidgets = getAllWidgets(items);
            const widgetById = new Map(allWidgets.map(widget => [widget.id, widget]))
            const sectionById = new Map(
                allWidgets
                    .filter(item => item.formType === "section" && item.id)
                    .map(section => [section.id, section])
            )
            const sectionByLabel = new Map(
                allWidgets
                    .filter(item => item.formType === "section" && item.label)
                    .map(section => [section.label, section])
            )
            formElements = allWidgets
                .filter(item => !columnsNotToIncludeInView.includes(item.formType))
                .map(widget => {
                    const parentTable = widget.parentTableId ? widgetById.get(widget.parentTableId) : undefined
                    const sectionPath = parentTable?.sectionPath || widget.sectionPath || []
                    const parentSections = sectionPath
                        .map(step => (step.id && sectionById.get(step.id)) || (step.label && sectionByLabel.get(step.label)))
                        .filter(Boolean)
                    return {
                        ...widget,
                        ...(parentTable ? { parentTable } : {}),
                        parentSection: parentSections[parentSections.length - 1],
                        parentSections,
                    }
                })
        }
        setDefinedWidgets(formElements)
    }, [workflow.workflowId, userInfo])
    useEffect(() => {
        if (userInfo.userId && workflow?.workflowId) {
            getAllDefinedWidgets();
            showLoader(false);
        } else {
            showLoader(true);
        }
    }, [userInfo, workflow, getAllDefinedWidgets])

    const fetchSpaceWorkflow = useCallback(async () => {
        const response = await getSpaceWorkflow(spaceId);
        if (response.status === 200 && response.data) {
            setWorkflow(response.data)
            if (response.data.node) {
                let endNode = response.data?.node?.find(step => step.data?.type === "end")
                if (endNode) {
                    setWorkflowIsCompleted(true)
                }
            }
        }

    }, [spaceId])

    const fetchFormConditions = useCallback(async (workflowId) => {
        if (!workflowId) return;
        try {
            const response = await getAllFormConditionForWorkflow(workflowId);
            if (response.status === 200) {
                setFormConditions(response.data || []);
            } else {
                setFormConditions([]);
            }
        } catch (error) {
            setFormConditions([]);
        }
    }, [])

    useEffect(() => {
        if (currentSpaceObj && currentSpaceObj.spaceId) {
            let defaultBreadCrumbItems = [{ title: <HomeOutlined />, path: userInfo.isAdmin ? "/app/dashboard" : "/app/dashboard" }]
            let bdItems = [...defaultBreadCrumbItems];
            bdItems.push({ title: currentSpaceObj.name })
            // bdItems.push({ title: 'All requests' })
            setBreadCrumbs(bdItems)
        }
    }, [currentSpaceObj, userInfo.isAdmin])

    useEffect(() => {
        fetchSpaceWorkflow();
    }, [fetchSpaceWorkflow, selectedSpace]);

    useEffect(() => {
        if (workflow?.workflowId) {
            fetchFormConditions(workflow.workflowId);
        }
    }, [workflow?.workflowId, fetchFormConditions]);

    useEffect(() => {
        if (searchKey) {
            const timer = setTimeout(() => {
                setIsSearchLoading(false)
            }, [1000])
            return () => {
                clearTimeout(timer);
            }
        } else {
            setIsSearchLoading(false)
        }
    }, [searchKey])

    const getColumnsAndFilters = useCallback(() => {
        if (!(selectedView.selectedColumns && selectedView.selectedColumns.length > 0)) return;
        setIsViewColumnsLoading(true);

        // shallow compare helper for columns by dataIndex/title to avoid redundant state
        const sameColumns = (a = [], b = []) => {
            if (a === b) return true;
            if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) {
                const x = a[i] || {}; const y = b[i] || {};
                if (x.dataIndex !== y.dataIndex || x.title !== y.title) return false;
            }
            return true;
        };

        let reqCols = [];
        let colFilters = {};

        const renderMergedTableCell = (text) => {
            const displayText = text ? String(text) : "";
            return (
                <TruncatedCell text={displayText} label={t('Table Column')} />
            );
        };

        const resolveWidgetForColumn = (columnId) => {
            const directWidget = definedWidgets.find(widget => widget.id === columnId);
            if (directWidget) {
                return directWidget;
            }
            return definedWidgets.find(widget => widget?.parentTableId && widget?.id?.endsWith(`::${columnId}`));
        };

        if (selectedView.default) {
            const defCols = allColumns.map(col => ({ ...col, title: col.title }));
            const formattedWidgetsCols = definedWidgets
                .filter(widget => widget.isOrgStructure)
                .map(widget => ({ ...buildTruncatedColumn(widget.id, getWidgetDisplayLabel(widget, viewerLanguage, t) || widget.label), isWidget: true }));
            const colsFromWidgets = definedWidgets
                .filter(widget => widget.isOrgStructure)
                .map(widget => ({ id: widget.id, label: getWidgetDisplayLabel(widget, viewerLanguage, t) || widget.label }));
            reqCols = defCols.concat(formattedWidgetsCols);
            setTableConfig(prev => {
                if (sameColumns(prev.columns, reqCols)) return prev;
                return { ...prev, columns: reqCols };
            });
            setSelectedFilters({});
            setSelectedColumns(selectedView.selectedColumns.concat(colsFromWidgets));
        } else {
            selectedView.selectedColumns.forEach(col => {
                let reqObj = allColumns.find(column => column.dataIndex === col.id);
                if (!reqObj) {
                    const colObjFromDefinedWidgets = resolveWidgetForColumn(col.id);
                    const isTableColumn = Boolean(colObjFromDefinedWidgets?.parentTableId || colObjFromDefinedWidgets?.id?.includes("::"));
                    const resolvedLabel = getWidgetDisplayLabel(colObjFromDefinedWidgets, viewerLanguage, t)
                        || col?.label
                        || col?.id;

                    reqObj = buildTruncatedColumn(colObjFromDefinedWidgets?.id || col.id, resolvedLabel);
                    if (isTableColumn) {
                        reqObj.render = renderMergedTableCell;
                    }
                }
                // Uncomment when api will return appliedFilters
                // const colFilterObj = selectedView?.appliedFilters?.find(c => c.column === reqObj.dataIndex);
                // if (colFilterObj) {
                //   reqObj["filteredValue"] = colFilterObj.value;
                //   if (colFilterObj.column === "createdOn") {
                //     reqObj["filteredValue"] = colFilterObj.value.map(val => moment(new Date(val)));
                //   }
                //   colFilters[reqObj.dataIndex] = colFilterObj.value;
                // }
                if (reqObj?.dataIndex) {
                    reqCols.push(reqObj);
                }
            });
            setSelectedColumns(selectedView.selectedColumns || []);
            setTableConfig(prev => {
                if (sameColumns(prev.columns, reqCols)) return prev;
                return { ...prev, columns: reqCols };
            });
            setSelectedFilters(colFilters);
        }

        // Reset the flag after columns are loaded
        setTimeout(() => setIsViewColumnsLoading(false), 50);
    }, [selectedView, definedWidgets, allColumns, viewerLanguage, t])

    useEffect(() => {
        if (userInfo?.userId && selectedView?.viewId) {
            setIsManualViewSelection(true)  // Set flag when processing a view change
            getColumnsAndFilters();
        }
    }, [selectedView, definedWidgets, userInfo.userId, getColumnsAndFilters])

    useEffect(() => {
        if (documents?.length > 0 && spaceSpecificUserPickers?.length > 0) {
            let documentsList = documents.map(request => {
                let reqValues = getFormattedRequestValues({ ...request.value }, spaceSpecificUsers, spaceSpecificGroups, spaceSpecificUserPickers, getRowFormWidgets(request, definedWidgets))
                return { ...request, createdBy: request?.createdBy?.name, ...reqValues }
            })
            setDocuments(documentsList)
        }
    }, [spaceSpecificUserPickers])

    const fetchDocumentView = useCallback(async (userId) => {
        showLoader(true)
        let viewsResponse = await getSpaceViews(spaceId, userId);
        showLoader(false)

        if (viewsResponse?.status && viewsResponse.data && Array.isArray(viewsResponse.data)) {

            let viewData = viewsResponse.data?.filter(view => view.status === "active");
            let selView = viewData?.find(view => view.defaultViewers && view.defaultViewers.includes(userId));
            setIsManualViewSelection(true) // Set flag when loading initial view

            let selectedViewForState;
            if (selView) {
                selectedViewForState = { ...selView, label: selView.name, value: selView.viewId }
                setSelectedView(selectedViewForState)
                selectedViewForState = { ...selView, label: selView.name, value: selView.viewId }
                setSelectedView(selectedViewForState)
                setInitialDefaultView({ ...selView })
            } else {
                let defView = viewData.find(view => view.default)
                selectedViewForState = { ...defView, label: defView?.name, value: defView?.viewId }
                setSelectedView(selectedViewForState)
                selectedViewForState = { ...defView, label: defView?.name, value: defView?.viewId }
                setSelectedView(selectedViewForState)
                setInitialDefaultView({ ...defView })
            }

            let allViews = viewData.map(view => {
                return { ...view, label: view.name, value: view.viewId }
            })

            // Set availableViews to all views for dropdown
            setAvailableViews([...allViews])
            // Set spaceViews to only the selected view
            setSpaceViews([selectedViewForState])
        }
    }, [spaceId])

    useEffect(() => {
        if (selectedSpace && userInfo.userId) {
            fetchDocumentView(userInfo.userId);
        }
    }, [selectedSpace, userInfo, fetchDocumentView])

    const updateView = async (viewObj, showMessage = false) => {
        showLoader(true)
        const api = `${BACKEND_API}/view?spaceId=${selectedSpace}`

        await axios({
            method: "PUT",
            url: api,
            data: viewObj,
            headers: {
                userId: userInfo.userId,
                tenantId: 1
            },
        })
            .then((response) => {
                let data = response.data
                if (typeof data !== 'string') {
                    if (showMessage) {
                        message.success(t('Default view updated successfully.'))
                        showLoader(false)
                        fetchDocumentView(userInfo.userId);
                    }
                } else {
                    message.error(t('Failed to set default view. Please try again.'))
                }
            })
            .catch((error) => {
            });
        showLoader(false)
    }

    // Move this user's default list view from one view to another.
    // PUT /defaultViewers takes { userId, sourceViewId, destinationViewId } — the same
    // contract as /graph/default-viewers — so a single call replaces the old pattern of
    // patching the defaultViewers array on the previous and the new view separately.
    // sourceViewId is "" when the user has no default view yet.
    const updateDefaultViewer = async (sourceViewId, destinationViewId) => {
        showLoader(true)
        // Read at call time rather than using the module-level `tenantId`, which is
        // captured when this file is first imported — possibly before login has
        // written the tenant to localStorage.
        const currentTenantId = localStorage.getItem("tenantId") || tenantId
        await axios({
            method: "PUT",
            url: `${BACKEND_API}/defaultViewers`,
            data: {
                userId: userInfo.userId,
                sourceViewId: sourceViewId || "",
                destinationViewId,
                tenantId: currentTenantId
            },
            headers: {
                userId: userInfo.userId,
                tenantId: currentTenantId
            },
        })
            .then((response) => {
                let data = response.data
                if (typeof data !== 'string') {
                    message.success(t('Default view updated successfully.'))
                    showLoader(false)
                    fetchDocumentView(userInfo.userId);
                } else {
                    message.error(t('Failed to set default view. Please try again.'))
                }
            })
            .catch((error) => {
            });
        showLoader(false)
    }

    const saveDocumentView = (view, isNew = false) => {
        // return;
        let postSelectedColumns = selectedColumns.map(col => {
            let colObj = defaultViewColumns.find(defCol => defCol.id === col.id)
            const widget = definedWidgets.find(defWidget => defWidget.id === col.id)
            const systemCol = defaultView.selectedColumns.find(sysCol => sysCol.id === col.id)
            return {
                id: colObj?.id || col.id,
                label: widget?.label || systemCol?.label || colObj?.label
            }
        })
        let method = view.viewId ? "PUT" : "POST"
        showLoader(true)
        axios({
            method: method,
            url: method === "PUT" ? `${BACKEND_API}/view?spaceId=${spaceId}` : `${BACKEND_API}/view`,
            data: { ...view, selectedColumns: postSelectedColumns },
            headers: {
                userId: userInfo.userId,
                tenantId: 1
            },
        })
            .then(async (response) => {
                let data = response.data
                if (data && typeof data !== 'string') {
                    let label = view.viewId ? 'updated' : 'created'
                    message.success('View ' + label + ' successfully.')
                    if (isNew) {
                        // The POST body already carries defaultViewers, so the new view is
                        // saved as this user's default in that one call — no follow-up
                        // request is made from here.
                        await fetchDocumentView(userInfo.userId);
                    } else {
                        fetchDocumentView(userInfo.userId);
                    }
                } else {
                    message.error(t('Failed to update view. Please try again.'))
                }
                showLoader(false)
            })
            .catch((error) => {
                showLoader(false);

                // Handle specific error cases
                if (error.response?.status === 409) {
                    message.error(t('View with same name already exists. Please choose a different name.'));
                } else if (error.response?.data?.message) {
                    message.error(error.response.data.message);
                } else {
                    message.error(t('Failed to save view. Please try again.'));
                }
            });
        showLoader(false)
    }

    const handleViewSelector = () => {
        // Allow customizing columns even for unsaved (custom) views – requirement change
        setViewModal(true)
    }

    const navigateToSettings = (tab) => {
        navigate(`/app/space/${selectedSpace}/${tab}`);
    }

    const createNewRequest = () => {
        if (!workflowIsCompleted) {
            message.error(t('The workflow is not completed. Please complete the workflow first.'))
            return
        }
        navigate(`/app/space/${spaceId}/submission/${workflow.workflowId}/new`)
    }

    const onSearch = async (value) => {
        setIsTableLoading(true)
        setIsSearchDone(true)
        let reqSearchParams = { 'caseId': [value] }
        let searchResultResponse = await getAdvanceSearchDocuments({ filters: reqSearchParams }, spaceId, 1, pageSize)

        setIsTableLoading(false)
        if (searchResultResponse.status === 200) {
            let docs = searchResultResponse.data?.requests || []
            let documentsList = docs.map(request => {
                let reqValues = getFormattedRequestValues({ ...request.value }, spaceSpecificUsers, spaceSpecificGroups, spaceSpecificUserPickers, getRowFormWidgets(request, definedWidgets))
                return { ...request, createdBy: request.createdBy.name, ...reqValues }
            })
            if (docs.length === 0) {
                message.info(t('No records found.'))
            }
            setDocuments(documentsList)
            setPageNumber(1)
            setTotalRecords(documentsList.length)
        }
    }

    const onChangeSearch = (e) => {
        let value = e.target.value
        setIsSearchLoading(true)
        if (value) {
            setSearchKey(value);
        } else {
            setSearchKey("");
        }
    }
    const closeSearch = () => {
        // setShowSearch(false)
    }

    // `view` is passed explicitly when the caller already knows the new view
    // (view switch), since the selectedView state update isn't visible yet.
    const getDocumentsList = useCallback(async (widgets = definedWidgets, pgNumber = pageNumber, pgSize = pageSize, view = selectedView) => {
        let viewId = ""
        if (!view.isDefault && view.value !== "custom") {
            viewId = view.viewId
        } else if (view?.value === "custom") {
            viewId = initialDefaultView?.viewId
        }
        setIsTableLoading(true)
        let requests = await getRequests(spaceId, workflow.workflowId, pgNumber, pgSize, viewId)
        setIsTableLoading(false)
        setTotalRecords(requests.data?.totalCount || 0)
        setStatusCounts(requests.data?.statusCounts || [])
        if (requests.data?.totalCount > 0) {
            let docs = requests.data.requests
            let documentsList = docs.map(request => {
                let reqValues = getFormattedRequestValues({ ...request.value }, spaceSpecificUsers, spaceSpecificGroups, spaceSpecificUserPickers, getRowFormWidgets(request, widgets))
                return { ...request, createdBy: request.createdBy.name, ...reqValues, value: request.value, workflow: request.workflow }
            })
            setDocuments(documentsList)
        } else {
            setDocuments([])
        }
    }, [spaceId, initialDefaultView, selectedView, workflow.workflowId, pageNumber, pageSize, spaceSpecificUsers, spaceSpecificGroups, spaceSpecificUserPickers, definedWidgets])

    const fetchGraphData = useCallback(async () => {
        if (!workflow?.workflowId) return;
        if (!totalRecords || totalRecords <= 0) { setGraphData([]); return; }
        showLoader(true)
        const res = await getRequestsForGraph(spaceId, workflow.workflowId, totalRecords)
        showLoader(false)
        if (res?.status === 200 && res.data?.totalCount >= 0) {
            const docs = res.data.requests || []
            const documentsList = docs.map(request => {
                const reqValues = getFormattedRequestValues({ ...request.value }, spaceSpecificUsers, spaceSpecificGroups, spaceSpecificUserPickers, getRowFormWidgets(request, definedWidgets))
                return { ...request, createdBy: request.createdBy.name, ...reqValues }
            })
            setGraphData(documentsList)
        } else {
            setGraphData([])
        }
    }, [spaceId, workflow, totalRecords, spaceSpecificUsers, spaceSpecificGroups, spaceSpecificUserPickers, definedWidgets])

    // Load graph data whenever Graph view is active and its prerequisites are ready.
    // Covers both an explicit switch to Graph and a saved default of Graph on first load.
    useEffect(() => {
        if (currentViewMode === 'graph' && workflow?.workflowId) {
            fetchGraphData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentViewMode, workflow?.workflowId, totalRecords])

    // Re-apply the per-space default view mode when the space changes (component may not remount)
    useEffect(() => {
        const def = readDefaultViewMode(userInfo?.userId || currentUserId(), spaceId);
        setDefaultViewMode(def);
        setCurrentViewMode(def || 'table');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spaceId])

    useEffect(() => {
        if (workflow.workflowId && spaceSpecificUsers?.length > 0 && initialDefaultView?.viewId) {
            getDocumentsList(definedWidgets);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflow.workflowId, spaceSpecificUsers.length, definedWidgets.length, initialDefaultView]);

    // removed unused handleClickOutside

    const handleViewChange = async (val) => {
        setIsManualViewSelection(true)

        // If it's a custom view, just set it directly
        if (val.value === 'custom') {
            setSelectedView(val)
            setSpaceViews([val]) // Only show the custom view
            setFiltersChanged(false)
            return
        }

        // For actual saved views, fetch the detailed view data
        if (val.viewId) {
            showLoader(true)
            try {
                const viewResponse = await getSingleView(spaceId, val.viewId, userInfo.userId)
                showLoader(false)

                if (viewResponse?.status && viewResponse.data) {
                    const detailedView = {
                        ...viewResponse.data,
                        label: viewResponse.data.name,
                        value: viewResponse.data.viewId
                    }
                    setSelectedView(detailedView)
                    // Update spaceViews to only contain the selected view
                    setSpaceViews([detailedView])
                    // The list is view-scoped, so reload it for the newly picked
                    // view. Passed explicitly since setSelectedView isn't applied yet.
                    setPageNumber(1)
                    await getDocumentsList(definedWidgets, 1, pageSize, detailedView)
                } else {
                    message.error(t('Failed to load view details'))
                }
            } catch (error) {
                showLoader(false)
                message.error(t('Failed to load view details'))
            }
        } else {
            // Fallback for views without viewId
            setSelectedView(val)
            setSpaceViews([val])
            setPageNumber(1)
            await getDocumentsList(definedWidgets, 1, pageSize, val)
        }

        setFiltersChanged(false)
    }

    const saveAsDefaultView = async (e, view) => {
        e.preventDefault();
        e.stopPropagation();
        // return
        setIsManualViewSelection(true)
        setSelectedView(view)
        // Only send a source when this user actually holds a default today; otherwise "".
        const sourceViewId = initialDefaultView?.defaultViewers?.includes(userInfo.userId)
            ? initialDefaultView.viewId
            : ""
        await updateDefaultViewer(sourceViewId, view.viewId)
    }

    const handleChange = (pagination, filters, sorter, extra) => {
        // Reset flags since this is a user-initiated filter change
        setIsManualViewSelection(false)
        setIsViewColumnsLoading(false)

        let selFilters = {};
        Object.keys(filters).forEach(key => {
            if (filters[key]) {
                selFilters[key] = filters[key]
            }
        })
        // Only update when filters actually changed
        setSelectedFilters(prev => {
            const sameKeys = Object.keys(prev).length === Object.keys(selFilters).length && Object.keys(prev).every(k => JSON.stringify(prev[k]) === JSON.stringify(selFilters[k]));
            return sameKeys ? prev : selFilters;
        })
        let updatedTableConfig = { ...tableConfig };
        let changeInFilters = false
        updatedTableConfig.columns.forEach(col => {
            if (selFilters[col.dataIndex] && JSON.stringify(selFilters[col.dataIndex]) !== JSON.stringify(col.filteredValue)) {
                changeInFilters = true
            }
            if (selFilters[col.dataIndex]) {
                col.filteredValue = selFilters[col.dataIndex]
            } else {
                col.filteredValue = []
            }
        });

        setFiltersChanged(changeInFilters)
        // Avoid setting if columns array is referentially the same (we mutated in place above intentionally)
        setTableConfig(prev => (prev === updatedTableConfig ? prev : updatedTableConfig))
    }

    const saveSelectedColumns = (selectedCols) => {
        // Reset flags since this is a user-initiated column change
        setIsManualViewSelection(false)
        setIsViewColumnsLoading(false)

        let updatedTableConfig = { ...tableConfig };
        let newColumns = []
        selectedCols.forEach(col => {
            const existing = tableConfig.columns.find(column => column.dataIndex === col)
            const catalogueCol = defaultViewColumns.find(viewCol => viewCol.id === col)
            const systemCol = allColumns.find(column => column.dataIndex === col)
            if (systemCol) {
                newColumns.push({
                    ...systemCol,
                    ...(existing && Object.prototype.hasOwnProperty.call(existing, "filteredValue")
                        ? { filteredValue: existing.filteredValue }
                        : {})
                })
                return
            }
            const label = catalogueCol?.label || existing?.title || col
            const colObj = buildTruncatedColumn(catalogueCol?.id || existing?.dataIndex || col, label)
            const isTableColumn = String(catalogueCol?.id || existing?.dataIndex || col).includes("::")
            if (isTableColumn && existing?.render) {
                colObj.render = existing.render
            }
            if (existing?.filteredValue) {
                colObj.filteredValue = existing.filteredValue
            }
            newColumns.push(colObj)
        })

        // Only set when columns actually changed
        const same = JSON.stringify(updatedTableConfig.columns) === JSON.stringify(newColumns);
        if (!same) {
            updatedTableConfig["columns"] = newColumns
            setTableConfig(updatedTableConfig)
            // Mark that filters/columns differed from the applied view so effect can flip to Custom View
            setFiltersChanged(true)
        }
        setViewModal(false)
        let updatedSelectedColumns = formatViewSelectedColumns(selectedCols, defaultViewColumns)
        setSelectedColumns(prev => {
            return JSON.stringify(prev) === JSON.stringify(updatedSelectedColumns) ? prev : updatedSelectedColumns;
        })
    }

    const saveDocView = (values, isNew, isDefault = false) => {
        let docFilters = []
        Object.keys(selectedFilters).forEach(key => {

            if (selectedFilters[key]) {
                let value = selectedFilters[key]
                if (key === 'createdOn') {
                    value = value.map(val => {
                        const parsed = safeMoment(val)
                        return parsed.isValid() ? parsed.format('MMM DD, YYYY') : val
                    })
                }
                // System columns (caseId, status, createdOn, ...) are not part of definedWidgets,
                // so currentWidget can legitimately be undefined here.
                let currentWidget = definedWidgets.find(widget => widget.id === key)
                if (currentWidget?.formType === "dropdownField" || currentWidget?.formType === "multipleChoiceField" || currentWidget?.formType === "radioField") {
                    value = value.map(val => {
                        let currentValOption = (currentWidget.options || []).find(opt => opt.label === val)
                        return currentValOption?.value || val
                    })
                }
                docFilters.push({ column: key, value })
            }
        })

        let postData = { name: values.name, selectedColumns: selectedColumns, appliedFilters: docFilters, translations: values.translations || {} }
        // If current mode is graph, attempt to include the persisted graph builder state
        try {
            const GRAPH_STORAGE_VERSION = 'ff_graphview_v1';
            const vid = (selectedView && selectedView.viewId) || (initialDefaultView && initialDefaultView.viewId) || 'x';
            const storageKey = `${GRAPH_STORAGE_VERSION}::${spaceId || 'x'}::${vid}`;
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.charts) {
                    postData.graphView = parsed; // attach charts + selectedId
                }
            }
        } catch (e) {
            // ignore storage errors
        }
        if (isNew) {
            postData.spaceId = spaceId
            postData.tenantId = tenantId
            postData.isDefault = true;
            postData.sharedWithUsers = values.sharedWithUsers || false
            postData.defaultViewers = [userInfo.userId]
        } else if (isDefault) {
            postData = { viewId: values.viewId, isDefault: values.isDefault }
        } else {
            postData.viewId = values.viewId
            if (values.sharedWithUsers !== selectedView.sharedWithUsers) {
                postData.sharedWithUsers = values.sharedWithUsers
            }
            postData.appliedFilters = docFilters
            postData.selectedColumns = selectedColumns
        }
        // return
        saveDocumentView(postData, isNew)
    }

    // Delete View
    const deleteDocumentView = async (view) => {
        showLoader(true)
        let deleteViewResponse = await deleteSpaceView(view, spaceId, userInfo.userId)
        showLoader(false)
        if (deleteViewResponse.status === 200) {
            message.success(deleteViewResponse.message)
            fetchDocumentView(userInfo.userId);
        } else {
            message.error(deleteViewResponse.message)
        }
    }

    const getDataForDownload = async (type) => {
        showLoader(true)
        let downloadUrl = `${BACKEND_API}/download-csv/${spaceId}?viewId=`
        if (!selectedView.default && selectedView.value !== "custom") {
            downloadUrl += selectedView?.viewId
        } else if (selectedView?.value === "custom") {
            downloadUrl += initialDefaultView?.viewId
        }
        if (selectedView.default) {
            // let addColumns = definedWidgets.map(widget => widget.label)
            downloadUrl += selectedView?.viewId 
            // + '&additionalColumns=' + addColumns.join(',')
        }
        let response = await axios({
            method: "GET",
            url: downloadUrl,
            headers: {
                userId: userInfo.userId,
                tenantId: 1
                //   "ngrok-skip-browser-warning": true
            },
        })
        let dataToDownload = response?.data?.result || []
        showLoader(false)
        // The API returns `result` as an array; previously we read `.data` on it which made it empty
        let reqDataToDownload = Array.isArray(dataToDownload) ? dataToDownload : (dataToDownload?.data || [])
        let columnsToDownload = tableConfig.columns
        // keep non-deleted and newest first
        reqDataToDownload = reqDataToDownload.filter(data => (data.status || data.Status) !== "deleted").reverse();

        const rowsForExport = reqDataToDownload.map((row) => {
            // Merge computed request values if present
            const reqValues = getFormattedRequestValues({ ...row }, spaceSpecificUsers, spaceSpecificGroups, spaceSpecificUserPickers, getRowFormWidgets(row, definedWidgets));

            return { ...reqValues }
        });
        const reqFileName = `Export - ${currentSpaceObj?.name || selectedSpace || 'Space'} - ${selectedView?.label || selectedView?.name || 'View'}`;
        exportExcel(columnsToDownload, rowsForExport, reqFileName)
    }

    const onRowClick = (record) => {
        let redirectPage = "approve"
        if (spaceEntitlements.includes("Workflow_Documents_Request_Update")) {
            navigate(`/app/space/${record.spaceId}/${redirectPage}/${record.workflowId}/${record.requestId}`)
        }
    }

    const onPageChange = async (page, pageSize) => {
        setPageNumber(page)
        setPageSize(pageSize)
        if (totalRecords > 0) {
            if (Object.keys(searchParams).length > 0) {
                await getSearchResult(page, pageSize)
            } else {
                await getDocumentsList(definedWidgets, page, pageSize);
            }
        }
    }

    const hydrateSelectedRowsWorkflow = useCallback(async (rows = []) => {
        if (!rows.length) return;
        const rowsMissingWorkflow = rows.filter(row => {
            const hasWorkflow = Array.isArray(row?.workflow) && row.workflow.length > 0;
            return !hasWorkflow && row?.requestId && !fetchedWorkflowRef.current.has(row.requestId);
        });
        if (rowsMissingWorkflow.length === 0) return;

        const loadingKeys = rowsMissingWorkflow.map(row => row.requestId);
        setSelectionLoadingKeys((prev) => Array.from(new Set([...prev, ...loadingKeys])));

        const fetchPromises = rowsMissingWorkflow.map(async (row) => {
            fetchedWorkflowRef.current.add(row.requestId);
            const response = await getSingleRequest(row.requestId);
            if (response?.status === 200 && response?.data) {
                return { requestId: row.requestId, data: response.data };
            }
            return null;
        });

        const results = await Promise.all(fetchPromises);
        const updates = results.filter(Boolean);
        if (updates.length === 0) {
            setSelectionLoadingKeys((prev) => prev.filter(key => !loadingKeys.includes(key)));
            return;
        }

        setDocuments((prev) => prev.map((doc) => {
            const match = updates.find(update => update.requestId === doc.requestId);
            if (!match) return doc;
            return {
                ...doc,
                workflow: match.data.workflow || doc.workflow,
                originalWorkflow: match.data.originalWorkflow || doc.originalWorkflow,
                value: match.data.value || doc.value,
                status: match.data.status || doc.status
            };
        }));

        setSelectionLoadingKeys((prev) => prev.filter(key => !loadingKeys.includes(key)));
    }, []);

    const handleSelectionChange = (keys, rows) => {
        setSelectedRowKeys(keys)
        setSelectedRows(rows || [])
        hydrateSelectedRowsWorkflow(rows || [])
    }

    // Handle cell value change during inline editing
    const handleCellChange = (record, dataIndex, value) => {
        const rowKey = record.requestId || record.caseId;
        setEditedRows((prev) => ({
            ...prev,
            [rowKey]: {
                ...record,
                ...prev[rowKey],
                [dataIndex]: value,
                value: {
                    ...(prev[rowKey]?.value || record.value || {}),
                    [dataIndex]: value,
                },
            },
        }));
    };

    // Whether any selected row has an actual field-level change (used to gate the Update button)
    const hasPendingChanges = useMemo(() => {
        return selectedRows.some(row => {
            const editedValues = editedRows[row.requestId]?.value
            if (!editedValues) return false
            const originalValue = row.value || {}
            return Object.keys(editedValues).some(fieldId => {
                const newValue = editedValues[fieldId]
                const oldValue = originalValue[fieldId]
                if (typeof newValue === 'object' && typeof oldValue === 'object') {
                    return JSON.stringify(newValue) !== JSON.stringify(oldValue)
                }
                return newValue !== oldValue
            })
        })
    }, [selectedRows, editedRows])

    // Handle bulk update of selected rows using mass-update API
    const handleBulkUpdate = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('Please select at least one row to update'))
            return
        }

        setIsBulkUpdating(true)
        showLoader(true)

        try {
            // Prepare update data with workflow structure for mass-update API
            const updates = selectedRows.map(row => {
                const editedData = editedRows[row.requestId] || {}
                const editedValues = editedData.value || {}
                const originalValue = row.value || {}
                const currentStatus = row.status || ""

                // Merge original values with edited values
                const mergedValue = { ...originalValue, ...editedValues }

                // Compute the difference - only include fields that were actually edited
                // and have a different value than the original
                const modCurrentValues = {}
                Object.keys(editedValues).forEach(fieldId => {
                    const newValue = editedValues[fieldId]
                    const oldValue = originalValue[fieldId]

                    // Only add to difference if the value actually changed
                    if (newValue !== oldValue) {
                        // Handle object comparison for complex values (like user pickers)
                        if (typeof newValue === 'object' && typeof oldValue === 'object') {
                            if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                                modCurrentValues[fieldId] = newValue
                            }
                        } else {
                            modCurrentValues[fieldId] = newValue
                        }
                    }
                })

                // Update the workflow array - only update the current status step's formValues
                let updatedWorkflow = [...(row.workflow || [])]
                updatedWorkflow = updatedWorkflow.map(step => {
                    if (step.name === currentStatus) {
                        // Merge existing step formValues with the difference values
                        const existingStepFormValues = step.formValues || {}
                        return {
                            ...step,
                            formValues: { ...existingStepFormValues, ...modCurrentValues }
                        }
                    }
                    return { ...step }
                })

                return {
                    requestId: row.requestId,
                    value: mergedValue,
                    workflow: updatedWorkflow
                }
            })

            // Call mass update API
            const response = await massUpdateRequests(spaceId, updates)

            if (response.status === 200) {
                message.success(`Successfully updated ${selectedRowKeys.length} row(s)`)
                // Clear edited rows, selection, and refresh data
                setEditedRows({})
                setSelectedRowKeys([])
                setSelectedRows([])
                getDocumentsList(definedWidgets, pageNumber, pageSize)
            } else {
                message.error(response.message || "Failed to update rows")
            }
        } catch (error) {
            message.error("Error updating rows: " + (error.message || "Unknown error"))
        } finally {
            setIsBulkUpdating(false)
            showLoader(false)
        }
    }

    const handleViewModeChange = (mode) => {
        setCurrentViewMode(mode); // Graph data is loaded by the effect above when mode === 'graph'
    }

    // Human label for a view mode
    const viewModeLabel = (mode) => (mode === 'kanban' ? t('Kanban View') : mode === 'graph' ? t('Graph View') : t('List View'))

    // Set the user's default view mode for this space (persisted server-side)
    const handleDefaultMenuClick = async ({ key }) => {
        const uid = userInfo?.userId || currentUserId();
        const previous = defaultViewMode;
        if (key === 'set') {
            if (!VALID_DEFAULT_MODES.includes(currentViewMode)) return;
            // Optimistically reflect the change, then persist.
            writeDefaultViewMode(uid, spaceId, currentViewMode);
            setDefaultViewMode(currentViewMode);
            const res = await updateSelectedView(spaceId, VIEW_MODE_TO_API[currentViewMode], uid);
            if (res.status >= 200 && res.status < 300) {
                message.success(t('Set as your default view'));
            } else {
                // Roll back optimistic update on failure.
                if (previous) writeDefaultViewMode(uid, spaceId, previous); else clearDefaultViewMode(uid, spaceId);
                setDefaultViewMode(previous || null);
                message.error(res.message || t('Failed to set default view. Please try again.'));
            }
        }
    }

    const updateSearchParams = (label, value) => {
        let updatedParams = { ...searchParams, [label]: value }
        setSearchParams(updatedParams)
    }
    const resetSearch = async () => {
        setSearchParams({})
        setPageNumber(1)
        hideAdvanceSearch()
        await getDocumentsList(definedWidgets, 1, pageSize)
    }
    const handleSearchClose = async () => {
        setSearchKey("")
        setShowSearch(false)
        if (isSearchDone) {
            setIsSearchDone(false)
            if (Object.keys(searchParams).length > 0) {
                await getSearchResult(1, pageSize)
            } else {
                await getDocumentsList(definedWidgets, 1, pageSize);
            }
        }
    }

    const hideAdvanceSearch = () => {
        setOpenAdvanceSearchModal(false)
    }

    const getSearchResult = async (pgNumber = 1, pgSize = pageSize) => {
        let reqSearchParams = {}
        setPageNumber(pgNumber)
        Object.keys(searchParams).map(key => {
            if (searchParams[key] && !Array.isArray(searchParams[key])) {
                reqSearchParams[key] = [searchParams[key]]
            } else if (searchParams[key] && searchParams[key].length > 0) {
                reqSearchParams[key] = searchParams[key]
            }
        })
        setOpenAdvanceSearchModal(false)

        // No active filters — drop the advance filters and fall back to the default list
        if (Object.keys(reqSearchParams).length === 0) {
            setSearchParams({})
            setPageNumber(1)
            await getDocumentsList(definedWidgets, 1, pgSize)
            return
        }

        setIsTableLoading(true)
        let searchResultResponse = await getAdvanceSearchDocuments({ filters: reqSearchParams }, spaceId, pgNumber, pgSize)
        setIsTableLoading(false)
        if (searchResultResponse.status !== 200) {
            message.error(t('No requests found'));
            setDocuments([])
            setTotalRecords(0)
            setStatusCounts([])
        } else {
            let requestData = searchResultResponse.data

            setTotalRecords(requestData?.totalCount || 0)
            setStatusCounts(requestData?.statusCounts || [])
            let docs = requestData?.requests || []
            let documentsList = docs.map(request => {
                let reqValues = getFormattedRequestValues({ ...request.value }, spaceSpecificUsers, spaceSpecificGroups, spaceSpecificUserPickers, getRowFormWidgets(request, definedWidgets))
                return { ...request, createdBy: request.createdBy.name, ...reqValues }
            })
            setDocuments(documentsList)
        }

    }

    // Statuses that are terminal / non-editable — checkboxes are disabled for these rows
    const NON_SELECTABLE_STATUSES = ["deleted", "archived", "draft", "rejected"]

    const getCheckboxProps = (record) => {
        const status = record?.status || ""
        // Direct match for deleted / archived / draft / rejected
        if (NON_SELECTABLE_STATUSES.includes(status)) {
            return { disabled: true }
        }
        // "Approved" is identified by an end-node whose name matches the record's status
        const nodes = record?.originalWorkflow?.nodes || []
        const isApproved = nodes.some(node => node.data?.type === "end" && node.data?.name === status)
        return { disabled: isApproved }
    }

    const getStatusIcons = (status) => {
        let icon = null;
        let statusLabel = status?.toLowerCase() || "";

        switch (statusLabel) {
            case "approved":
                icon = <CheckCircleOutlined className="w-4 h-4" />;
                break;
            case "refused":
                icon = <CloseCircleOutlined className="w-4 h-4" />;
                break;
            case "deleted":
                icon = <DeleteOutlined className="w-4 h-4" />;
                break;
            case "archived":
                icon = <InboxOutlined className="w-4 h-4" />;
                break;
            case "draft":
                icon = <FileTextOutlined className="w-4 h-4" />;
                break;
            default:
                icon = <EditOutlined className="w-4 h-4" />;
        }

        return icon;
    };

    return (
        <div style={WrapperStyle} className="document-container">
            {/* Hide breadcrumb in payment schedule view */}
            <BreadCrumb items={breadcrumbs} marginb="24px" />

            {/* Spaces dropdown with page actions  */}
            <Row gutter={8} style={{ marginBottom: "24px" }} className="spaces-card" >
                <Col span={6}>
                    <div className="text-h3 text-color-primary text-weight-600">{currentSpaceObj?.name}</div>
                </Col>
                <Col span={18} style={{ display: "flex", alignItems: "center", justifyContent: "end" }}>
                    {
                        userInfo?.isAdmin || spaceEntitlements.includes("Workflow_Documents_Search_Filter") ?
                            <div style={{ position: "relative", display: "flex" }}>
                                {
                                    showSearch &&

                                    <Input
                                        placeholder={t("Search by case ID")}
                                        onChange={onChangeSearch}
                                        className="doc-search"
                                        size="small"
                                        onBlur={closeSearch}
                                        onPressEnter={() => onSearch(searchKey)}
                                        suffix={<SearchOutlined />}
                                    />
                                }
                                {currentViewMode !== 'graph' &&
                                    <Tooltip title={t("Search")}>
                                        {
                                            showSearch ?
                                                <Button icon={<CloseOutlined />} style={{ display: "flex", justifyContent: "center", alignItems: "center", marginRight: "12px" }} onClick={handleSearchClose} />
                                                :
                                                <Button icon={<SearchOutlined />} disabled={isTableLoading} style={{ display: "flex", justifyContent: "center", alignItems: "center", marginRight: "12px" }} onClick={() => setShowSearch(true)} />
                                        }
                                    </Tooltip>
                                }
                            </div>
                            : null
                    }
                    {currentViewMode !== 'graph' &&
                        <Tooltip title={t("Advance Search")}>
                            <Button icon={<FilterOutlined />} onClick={() => setOpenAdvanceSearchModal(true)} style={{ display: "flex", justifyContent: "center", alignItems: "center", marginRight: "12px" }} />
                        </Tooltip>
                    }
                    {/* Keep space settings button visible in all views */}
                    {
                        userInfo?.isAdmin || spaceEntitlements.includes('Space_Update') ?
                            <Tooltip title={t("Space Settings")}>
                                <Button icon={<SettingOutlined />} style={{ display: "flex", justifyContent: "center", alignItems: "center", marginRight: "12px" }} onClick={() => navigateToSettings("space-settings")} />
                            </Tooltip>
                            : null
                    }
                    {
                        userInfo?.isAdmin || spaceEntitlements.includes('Space_Update') ?
                            <Tooltip title={t("Workflow")}>
                                <Button icon={<BranchesOutlined />} style={{ display: "flex", justifyContent: "center", alignItems: "center", marginRight: "12px" }} onClick={() => navigateToSettings("workflow")} />
                            </Tooltip>
                            : null
                    }

                    <>
                            {currentViewMode !== 'graph' &&
                                <Tooltip title={t("Customize Columns")}>
                                    <Button icon={<TableOutlined />} style={{ display: "flex", justifyContent: "center", alignItems: "center", marginRight: "12px" }} onClick={handleViewSelector} />
                                </Tooltip>
                            }
                            {
                                currentViewMode !== 'graph' && (userInfo?.isAdmin || spaceEntitlements.includes("Workflow_Documents_Download")) ?
                                    <Tooltip title={t("Download Requests")}>
                                        <Button icon={<DownloadOutlined />} style={{ display: "flex", justifyContent: "center", alignItems: "center", marginRight: "12px" }} onClick={getDataForDownload} />
                                    </Tooltip>
                                    : null
                            }
                            {/* {
                                spaceEntitlements.includes("Workflow_Documents_View_View") &&
                                } */}
                            {currentViewMode !== 'graph' &&
                                <ViewSelector
                                    views={spaceViews}
                                    availableViews={availableViews}
                                    selectedView={selectedView}
                                    handleViewChange={handleViewChange}
                                    saveAsDefaultView={saveAsDefaultView}
                                    saveDocView={saveDocView}
                                    userInfo={userInfo}
                                    spaceEntitlements={spaceEntitlements}
                                    deleteDocumentView={deleteDocumentView}
                                />
                            }
                        </>

                    {/* Bulk update buttons - visible when rows are selected */}
                    {selectedRowKeys.length > 0 && (
                        <>
                            <Button
                                style={{ marginLeft: "10px" }}
                                onClick={() => {
                                    setSelectedRowKeys([]);
                                    setSelectedRows([]);
                                    setEditedRows({});
                                }}
                            >
                                {t("Cancel")}
                            </Button>
                            <Button
                                type="primary"
                                className="submit-button"
                                style={{ marginLeft: "10px" }}
                                icon={<SaveOutlined />}
                                onClick={handleBulkUpdate}
                                loading={isBulkUpdating}
                                disabled={!hasPendingChanges}
                            >
                                {t("Update")}
                            </Button>
                        </>
                    )}

                    {/* Keep New button visible in all views */}
                    {(userInfo?.isAdmin || spaceEntitlements.includes('Workflow_Documents_Create')) && <Button type="primary" className="submit-button" style={{ marginLeft: "10px" }} icon={<PlusOutlined />} onClick={createNewRequest}>{t("New")}</Button>}
                </Col>

            </Row>
            {/* Documents List */}
            <div key={"docs-cols-" + JSON.stringify(tableConfig.columns)} className="dashboard-table relative">
                {/* Stats row with view tabs on the opposite side */}
                <div className="flex justify-between items-center gap-2 mb-3">
                    <div className="flex gap-2 items-center flex-wrap">
                        <div className="flex gap-1 items-center text-black status-count cursor-pointer" onClick={() => setShowStats(!showStats)}>
                            <UnorderedListOutlined className="w-4 h-4" style={{ color: "#141BCB" }} /> {t('All Requests')} <span className="text-gray-500 ml-1">{totalRecords}</span>
                            <span className="ml-1 h-full flex items-center text-gray-500" style={{ marginTop: "3px" }} >
                                {showStats ? <RightOutlined className="w-3 h-3" /> : <DownOutlined className="w-3 h-3" />}
                            </span>
                        </div>
                        {
                            showStats ? <>
                                {
                                    statusCounts.length > 0 ?
                                        <>
                                            <div className="flex status-count gap-4">
                                                {
                                                    statusCounts.map(status => {
                                                        return <div key={status.label} className="flex gap-[4px] items-center text-black capitalize">
                                                            <span style={{ color: "#141BCB" }}>{getStatusIcons(status.status)}</span> {status.status} <span className="text-gray-500 ml-1">{status.count}</span>
                                                        </div>
                                                    })
                                                }
                                            </div>
                                        </>
                                        : <span className="text-gray-600">{t('No stats available')}</span>
                                }
                            </>
                                : null
                        }
                    </div>
                    {/* View tabs: List | Kanban | Graph */}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Segmented
                            value={currentViewMode}
                            onChange={handleViewModeChange}
                            options={[
                                { label: t('List View'), value: 'table', icon: <UnorderedListOutlined /> },
                                { label: t('Kanban View'), value: 'kanban', icon: <AppstoreOutlined /> },
                                { label: t('Graph View'), value: 'graph', icon: <BarChartOutlined /> },
                            ].map((option) => (
                                // mark the user's default view with a star so it's identifiable without opening the menu
                                option.value === defaultViewMode
                                    ? {
                                        ...option,
                                        label: (
                                            <Tooltip title={t('Your default view')}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                    {option.label}
                                                    <StarFilled style={{ fontSize: 11, color: '#faad14' }} />
                                                </span>
                                            </Tooltip>
                                        ),
                                    }
                                    : option
                            ))}
                        />
                        <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            menu={{
                                onClick: handleDefaultMenuClick,
                                items: [
                                    {
                                        key: 'header',
                                        disabled: true,
                                        label: (
                                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {defaultViewMode ? `${t('Default')}: ${viewModeLabel(defaultViewMode)}` : t('No default view set')}
                                            </span>
                                        ),
                                    },
                                    { type: 'divider' },
                                    {
                                        key: 'set',
                                        icon: <StarOutlined />,
                                        disabled: !VALID_DEFAULT_MODES.includes(currentViewMode) || defaultViewMode === currentViewMode,
                                        label: t('Set current view as default'),
                                    },
                                ],
                            }}
                        >
                            <Tooltip title={t('Default view options')}>
                                <Button type="default" aria-label={t('Default view options')} icon={<MoreOutlined />} />
                            </Tooltip>
                        </Dropdown>
                    </span>
                </div>
                {currentViewMode === 'kanban' ? (
                    <KanbanBoard
                        documents={documents}
                        statuses={kanbanStatuses}
                        loading={isTableLoading}
                        onCardClick={onRowClick}
                        currentPage={pageNumber}
                        pageSize={pageSize}
                        total={totalRecords}
                        onPageChange={onPageChange}
                    />
                ) : (
                    <>
                        <TableComponent
                            selectable={true}
                            rowKey="requestId"
                            selectedRowKeys={selectedRowKeys}
                            onSelectionChange={handleSelectionChange}
                            config={tableConfig}
                            data={documents}
                            userInfo={userInfo}
                            loader={isTableLoading}
                            onChange={handleChange}
                            onRowClick={onRowClick}
                            rowClickable={true}
                            currentPage={pageNumber}
                            pageSize={pageSize}
                            showCustomPagination={true}
                            total={totalRecords}
                            showPagination={false}
                            onPageChange={onPageChange}
                            viewMode={currentViewMode === 'kanban' ? 'table' : currentViewMode}
                            onViewModeChange={handleViewModeChange}
                            hideViewToggle={true}
                            availableColumns={defaultViewColumns}
                            graphData={graphData}
                            spaceId={spaceId}
                            formItems={definedWidgets}
                            viewId={selectedView?.viewId || selectedView?.value}
                            editable={selectedRowKeys.length > 0}
                            editedRows={editedRows}
                            onCellChange={handleCellChange}
                            formConditions={formConditions}
                            selectionLoadingKeys={selectionLoadingKeys}
                            getCheckboxProps={getCheckboxProps}
                        />
                    </>
                )}

                {/* Columns selector modal */}
                <Modal footer={false}
                    title={t('Customize Columns')}
                    centered
                    open={viewModal}
                    onOk={() => setViewModal(false)}
                    onCancel={() => setViewModal(false)}
                    width={600}
                    className="columns-selector-modal"
                >
                    <ColumnsSelector defaultViewColumns={defaultViewColumns} defaultSelectedColumns={selectedColumns} saveSelectedColumns={saveSelectedColumns} />
                </Modal>

                <Modal className="advance-search-modal" open={openAdvanceSearchModal} onCancel={hideAdvanceSearch} title={t("Advance Search")} >
                    <SearchContainer setShowSearch={setOpenAdvanceSearchModal} searchKey={searchKey} workflow={workflow} selectedView={initialDefaultView} definedWidgets={definedWidgets} defaultViewColumns={defaultViewColumns} userInfo={userInfo} searchParams={searchParams} updateSearchParams={updateSearchParams} getSearchResult={getSearchResult} resetSearch={resetSearch} />
                </Modal>
            </div>
        </div>
    )
}

export default DocumentsList
